import { useState, useEffect } from 'react';
import { publicApi } from '@/lib/api';
import { FadeIn } from '@/components/FadeIn';
import { useContentValue } from '@/contexts/SiteContentContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Coffee, Martini, Beer, Wine, GlassWater, FlaskConical, Loader2 } from 'lucide-react';

const ICON_MAP = { cocktails: Martini, kaffee: Coffee, bier: Beer, wein: Wine, softdrinks: GlassWater, spirituosen: FlaskConical };

function SimpleItem({ name, detail, description, price }) {
  return (
    <div className="flex items-baseline justify-between py-2.5 border-b border-white/5 last:border-0 gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-stone-200 font-body text-sm">{name}</span>
          {detail && <span className="text-stone-500 text-xs font-body">{detail}</span>}
        </div>
        {description && <p className="text-stone-600 text-xs font-body italic mt-0.5 leading-snug">{description}</p>}
      </div>
      <span className="text-amber-400 font-body font-semibold text-sm whitespace-nowrap">{price}</span>
    </div>
  );
}

function MultiPriceTable({ columns, items }) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full min-w-[340px]">
        <thead>
          <tr>
            <th className="text-left pb-2 pr-3" />
            {columns.map((col) => (
              <th key={col} className="text-right pb-2 px-1.5 text-stone-500 text-[11px] font-body font-medium uppercase tracking-wider whitespace-nowrap">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={`${item.name}-${i}`} className="border-b border-white/5 last:border-0">
              <td className="py-2.5 pr-3 text-stone-200 font-body text-sm">{item.name}</td>
              {(item.prices || []).map((p, j) => (
                <td key={j} className="py-2.5 px-1.5 text-right text-amber-400 font-body font-semibold text-sm whitespace-nowrap">{p || '–'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CocktailGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((item, i) => (
        <div key={`${item.name}-${i}`} className="flex items-start justify-between py-3 px-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-amber-400/15 transition-colors duration-300">
          <div className="flex-1 min-w-0 mr-3">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-stone-100 font-body font-medium text-sm">{item.name}</span>
              {item.size && <span className="text-stone-600 text-xs font-body">{item.size}</span>}
            </div>
            {item.description && <p className="text-stone-500 text-xs font-body italic mt-1 leading-snug">{item.description}</p>}
          </div>
          <span className="text-amber-400 font-body font-bold text-sm whitespace-nowrap">{item.price}</span>
        </div>
      ))}
    </div>
  );
}

function MenuSubSection({ section }) {
  const hasColumns = section.columns && section.columns.length > 0;
  return (
    <div className="mb-6 last:mb-0">
      {section.title && (
        <h4 className="text-stone-400 text-xs font-body font-semibold uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
          <span className="w-5 h-px bg-amber-400/40" />{section.title}
        </h4>
      )}
      {hasColumns ? (
        <MultiPriceTable columns={section.columns} items={section.items} />
      ) : (
        <div>{section.items.map((item, i) => <SimpleItem key={`${item.name}-${i}`} name={item.name} detail={item.detail} description={item.description} price={item.price} />)}</div>
      )}
    </div>
  );
}

function SpiritsAccordion({ sections }) {
  return (
    <Accordion type="multiple" className="space-y-2">
      {sections.map((section) => (
        <AccordionItem key={section.title} value={section.title} className="glass-card rounded-xl border-white/5 px-5 overflow-hidden">
          <AccordionTrigger className="py-4 text-stone-200 font-body font-medium text-sm hover:text-amber-400 hover:no-underline">
            <div className="flex items-center gap-3">
              <span>{section.title}</span>
              <span className="text-stone-600 text-xs font-normal">{section.items.length} Produkte</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <MultiPriceTable columns={section.columns} items={section.items} />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function MenuSection() {
  const [categories, setCategories] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [loading, setLoading] = useState(true);
  const c = useContentValue();

  useEffect(() => {
    publicApi.getMenu().then((res) => {
      const cats = res.data.categories || [];
      setCategories(cats);
      if (cats.length > 0) setActiveId(cats[0].slug);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = categories.find((c) => c.slug === activeId);
  const isSpirits = active?.cat_type === 'spirits';
  const isCocktails = active?.cat_type === 'cocktails';

  return (
    <section id="karte" data-testid="menu-section" className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1C1C1B]/50 to-transparent pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-12">
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase font-body">{c('Menü.Eyebrow', 'Unsere Karte')}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-100 mt-3 tracking-wide uppercase">{c('Menü.Überschrift', 'Getränke & Genuss')}</h2>
            <p className="text-stone-400 text-base max-w-lg mx-auto mt-4 font-body">{c('Menü.Beschreibung', 'Von handgemachten Cocktails bis zum perfekten Espresso — entdecke unsere Auswahl.')}</p>
          </div>
        </FadeIn>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={32} className="text-amber-400 animate-spin" /></div>
        ) : (
          <>
            <FadeIn delay={100}>
              <div className="flex overflow-x-auto gap-2 pb-2 mb-10 justify-start sm:justify-center">
                {categories.map((cat) => {
                  const Icon = ICON_MAP[cat.slug];
                  return (
                    <button key={cat.slug} onClick={() => setActiveId(cat.slug)} data-testid={`menu-tab-${cat.slug}`}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-body font-medium transition-all whitespace-nowrap shrink-0 ${activeId === cat.slug ? 'bg-amber-500 text-[#141413] shadow-lg shadow-amber-500/20' : 'glass-card text-stone-400 hover:text-stone-200'}`}>
                      {Icon && <Icon size={15} />}{cat.name}
                    </button>
                  );
                })}
              </div>
            </FadeIn>

            <FadeIn delay={200}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="hidden lg:block lg:col-span-3">
                  <div className="rounded-2xl overflow-hidden sticky top-28 relative" style={{ aspectRatio: '3 / 4' }}>
                    {categories.map((cat) => (
                      <img key={cat.slug} src={cat.image_url} alt={`${cat.name} im TreffPunkt Luzern`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${activeId === cat.slug ? 'opacity-100' : 'opacity-0'}`}
                        style={{ objectPosition: '50% 45%' }} />
                    ))}
                    <div className="absolute inset-0 pointer-events-none rounded-2xl shadow-[inset_0_0_40px_rgba(0,0,0,0.4)]" />
                  </div>
                </div>
                <div className="lg:col-span-9">
                  <div className="glass-card rounded-2xl p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      {ICON_MAP[activeId] && (() => { const I = ICON_MAP[activeId]; return <I size={20} className="text-amber-400" />; })()}
                      <h3 className="font-display text-2xl font-bold text-stone-100 tracking-wide uppercase">{active?.name}</h3>
                    </div>
                    {isCocktails && active?.sections?.[0] && <CocktailGrid items={active.sections[0].items} />}
                    {isSpirits && active?.sections && <SpiritsAccordion sections={active.sections.filter(s => s.title)} />}
                    {!isCocktails && !isSpirits && active?.sections?.map((sec, i) => <MenuSubSection key={sec.title || i} section={sec} />)}
                    <p className="text-stone-600 text-xs font-body mt-8 text-center">{c('Menü.Preishinweis', 'Alle Preise in CHF · Preisänderungen vorbehalten')}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </>
        )}
      </div>
    </section>
  );
}
